/**
 * Phase 15 Plan 15-02 — UploadMonthlyDocumentsDialog
 *
 * Modal for uploading two PDFs (Combined Daily Logsheet + Monthly Summary Logsheet)
 * to lock a (machine, month) tuple.
 *
 * Validation (Zod + RHF):
 *   - Both files required
 *   - Each file must be ≤ 10 MiB (T-15-20)
 *   - Each file must be application/pdf (or .pdf extension) (T-15-20)
 *
 * Error mapping on 409:
 *   - LOGSHEET_NOT_ALL_APPROVED      → friendly message
 *   - LOGSHEET_MONTH_ALREADY_LOCKED  → friendly message
 *   - other                          → generic toast with detail
 */

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useUploadMonthlyDocumentsMutation } from '@/hooks/useLogsheetMonthlyQueries';
import { ApiError } from '@/lib/api/error';
import { useZodForm } from '@/lib/forms/useZodForm';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { EligibleMonthlyTuple, UploadMonthlyDocumentsResult } from '@/types/logsheet-monthly';

// ---------------------------------------------------------------------------
// Validation schema (T-15-20)
// ---------------------------------------------------------------------------

const MAX_BYTES = 10 * 1024 * 1024; // 10 MiB
const PDF_MIME = 'application/pdf';

// Controller passes null when no file is selected; treat null/non-File as "required".
// z.custom accepts any value; superRefine emits the first applicable error and aborts
// using ctx.addIssue + return so later checks don't run on null.
const fileField = z.custom<File | null>().superRefine((val, ctx) => {
  if (!(val instanceof File)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'File is required' });
    return;
  }
  if (val.size <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'File must not be empty' });
    return;
  }
  if (val.size > MAX_BYTES) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'File must be ≤ 10 MB' });
    return;
  }
  if (val.type !== PDF_MIME && !val.name.toLowerCase().endsWith('.pdf')) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'File must be a PDF' });
  }
});

const schema = z.object({
  combinedDailyLogsheet: fileField,
  monthlySummaryLogsheet: fileField,
});

// The superRefine above ensures only File values pass validation, but the inferred
// type from z.custom<File | null> is still File | null. We cast at the submit handler.
interface UploadFormValues {
  combinedDailyLogsheet: File | null;
  monthlySummaryLogsheet: File | null;
}

// ---------------------------------------------------------------------------
// Error code → friendly message mapping
// ---------------------------------------------------------------------------

const ERROR_CODE_MESSAGES: Readonly<Record<string, string>> = {
  LOGSHEET_NOT_ALL_APPROVED:
    'Some entries for this month are not yet Approved. Please clear them before locking the month.',
  LOGSHEET_MONTH_ALREADY_LOCKED: 'This month is already locked.',
};

function mapApiError(error: ApiError): string {
  // The backend sets the title to the code string in problem+json
  const code = error.message;
  const mapped = ERROR_CODE_MESSAGES[code];
  if (mapped !== undefined) return mapped;
  return error.detail ?? error.message;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface UploadMonthlyDocumentsDialogProps {
  readonly open: boolean;
  readonly tuple: EligibleMonthlyTuple;
  readonly onClose: () => void;
  readonly onSuccess: (result: UploadMonthlyDocumentsResult) => void;
}

export function UploadMonthlyDocumentsDialog({
  open,
  tuple,
  onClose,
  onSuccess,
}: UploadMonthlyDocumentsDialogProps): JSX.Element {
  const [serverError, setServerError] = useState<string | null>(null);
  // REVIEW FF-012: track the active upload's AbortController so handleClose
  // and component unmount can cancel an in-flight multipart upload.
  const uploadAbortRef = useRef<AbortController | null>(null);

  const form = useZodForm({
    schema,
    mode: 'onChange',
    defaultValues: {
      combinedDailyLogsheet: null as unknown as File,
      monthlySummaryLogsheet: null as unknown as File,
    },
  });

  const mutation = useUploadMonthlyDocumentsMutation();

  function abortInflight(): void {
    if (uploadAbortRef.current !== null) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
  }

  function handleClose(): void {
    abortInflight();
    form.reset();
    setServerError(null);
    mutation.reset();
    onClose();
  }

  // Best-effort cancel on unmount — prevents the backend from completing a
  // month-lock the user clearly meant to cancel.
  useEffect(() => {
    return () => {
      abortInflight();
    };
  }, []);

  const onSubmit = form.handleSubmit((values: UploadFormValues) => {
    setServerError(null);
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    mutation.mutate(
      {
        machineId: tuple.machineId,
        month: tuple.month.slice(0, 7), // "YYYY-MM-01" → "YYYY-MM"
        // handleSubmit only calls this when validation passed, so values are non-null Files.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        combinedDailyLogsheet: values.combinedDailyLogsheet!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        monthlySummaryLogsheet: values.monthlySummaryLogsheet!,
        signal: controller.signal,
      },
      {
        onSuccess: (result) => {
          uploadAbortRef.current = null;
          onSuccess(result);
          handleClose();
        },
        onError: (error) => {
          uploadAbortRef.current = null;
          if (error instanceof ApiError) {
            setServerError(mapApiError(error));
          } else {
            setServerError('An unexpected error occurred.');
          }
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="upload-monthly-documents-dialog">
        <DialogHeader>
          <DialogTitle>Upload Monthly Documents</DialogTitle>
          <DialogDescription>
            Upload the Combined Daily Logsheet PDF and Monthly Summary Logsheet PDF to lock this
            month. Both files must be PDFs ≤ 10 MB each.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-4 py-2">
            {/* Readonly machine info */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Machine</Label>
                <p
                  className="rounded-md border bg-muted px-3 py-2 text-sm"
                  data-testid="upload-monthly-documents-dialog-machine-readonly"
                >
                  {formatMachineName(tuple.machineName, tuple.vehicleNumber, tuple.machineCode)}
                </p>
              </div>
              <div className="space-y-1">
                <Label>Month</Label>
                <p
                  className="rounded-md border bg-muted px-3 py-2 text-sm font-mono"
                  data-testid="upload-monthly-documents-dialog-month-readonly"
                >
                  {tuple.month.slice(0, 7)}
                </p>
              </div>
            </div>

            {/* Combined Daily Logsheet */}
            <div className="space-y-1">
              <Label htmlFor="upload-monthly-combined-daily">
                Combined Daily Logsheet (PDF)
                <span className="ml-1 text-destructive" aria-hidden="true">*</span>
              </Label>
              <Controller
                control={form.control}
                name="combinedDailyLogsheet"
                render={({ field: { onChange, onBlur, name, ref } }) => (
                  <input
                    id="upload-monthly-combined-daily"
                    type="file"
                    className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20"
                    data-testid="upload-monthly-documents-dialog-combined-daily-input"
                    name={name}
                    ref={ref}
                    onBlur={onBlur}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      onChange(file ?? null);
                    }}
                  />
                )}
              />
              {form.formState.errors.combinedDailyLogsheet !== undefined && (
                <p
                  className="text-xs text-destructive"
                  data-testid="upload-monthly-documents-dialog-error-combinedDailyLogsheet"
                >
                  {form.formState.errors.combinedDailyLogsheet.message}
                </p>
              )}
            </div>

            {/* Monthly Summary Logsheet */}
            <div className="space-y-1">
              <Label htmlFor="upload-monthly-summary">
                Monthly Summary Logsheet (PDF)
                <span className="ml-1 text-destructive" aria-hidden="true">*</span>
              </Label>
              <Controller
                control={form.control}
                name="monthlySummaryLogsheet"
                render={({ field: { onChange, onBlur, name, ref } }) => (
                  <input
                    id="upload-monthly-summary"
                    type="file"
                    className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20"
                    data-testid="upload-monthly-documents-dialog-monthly-summary-input"
                    name={name}
                    ref={ref}
                    onBlur={onBlur}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      onChange(file ?? null);
                    }}
                  />
                )}
              />
              {form.formState.errors.monthlySummaryLogsheet !== undefined && (
                <p
                  className="text-xs text-destructive"
                  data-testid="upload-monthly-documents-dialog-error-monthlySummaryLogsheet"
                >
                  {form.formState.errors.monthlySummaryLogsheet.message}
                </p>
              )}
            </div>

            {/* Server error */}
            {serverError !== null && (
              <p
                className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                data-testid="upload-monthly-documents-dialog-server-error"
              >
                {serverError}
              </p>
            )}
          </div>

          {mutation.isPending && (
            <div
              className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted"
              aria-label="Uploading…"
            >
              <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
            </div>
          )}

          <DialogFooter className="mt-4">
            {/* REVIEW-2 NF-2: do NOT disable Cancel during mutation — the FF-012
                AbortController is the ONLY way to actually stop an in-flight
                upload, and disabling the button made it unreachable.
                Surface "Cancel upload" while pending to make the affordance
                explicit. */}
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              data-testid="upload-monthly-documents-dialog-cancel"
            >
              {mutation.isPending ? 'Cancel upload' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !form.formState.isValid}
              data-testid="upload-monthly-documents-dialog-submit"
            >
              {mutation.isPending ? 'Uploading…' : 'Upload & Lock Month'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
